import React, { useContext, useReducer } from "react";
import EmailForm from "./EmailForm";
import VerifyOtpModal from "./VerifyOtpModal";
import { NewVerifyOtpModal } from "../../../NewVerifyOtpModal";
import { MESSAGE, MESSAGE_TYPE } from "../../../../constants/messages";
import { useCustomerAccount } from "../../AccountProvider";
import { useCommonContext } from "../../CommonProvider";
import { handleApiError } from "../../../../utils/errors";
import { LoadingContext } from "../../LoadingProvider";
import EpcModal from "./EpcModal";
import { askForEmailChange, askForEmailPreferences, updateOldEmailPreferences } from "../../../../api/custAccountApi";
import { hasPreferences, setOldPreferencesToOptOut } from "./data";
import analytics from "../../../../utils/analytics";
import { isNativeApp, postDataToEventBus } from "../../../../utils";
import {ApiContext} from "../../../../contexts/ApiContext";
import {useGoogleReCaptcha} from "react-google-recaptcha-v3";

//region actions

const ACTIONS = {
  INIT_OTP_MODAL: "INIT_OTP_MODAL",
  CLOSE_TRANSFER_PREFERENCES_MODAL: "CLOSE_TRANSFER_PREFERENCES_MODAL",
  TRANSFER_PREFERENCES_SUCCESS: "TRANSFER_PREFERENCES_SUCCESS",
  CLOSE_MODAL: "CLOSE_MODAL",
  VERIFY_OTP_SUCCESS: "VERIFY_OTP_SUCCESS",
  VERIFY_OTP_FAILURE: "VERIFY_OTP_FAILURE",
  SET_EVOICE_ERROR: "SET_EVOICE_ERROR",
  INIT_OTP_CHOICE: "INIT_OTP_CHOICE",
  UPDATE_AUTH_ID: "UPDATE_AUTH_ID"
}

const initialState = {
  showOtpModal: false,
  showTransferPreferencesModal: false,
  oldEmail: "",
  oldEmailPreferences: {},
  newEmailPreferences: {},
  tableData: [],
  emailEditContext: {},
  submittedEmail: "",
  evoiceErrors: []
}

function initVerifyOtpAction(emailEditContext) {
  return {
    type: ACTIONS.INIT_OTP_MODAL,
    emailEditContext
  }
}

function initVerifyOtpChoiceModal(emailEditContext) {
  return {
    type: ACTIONS.INIT_OTP_CHOICE,
    emailEditContext
  }
}

function verifyOtpFailure() {
  return {
    type: ACTIONS.VERIFY_OTP_FAILURE,
  }
}

function cancelModal() {
  return {
    type: ACTIONS.CLOSE_MODAL,
  }
}

function closeEmailPreferenceTransferModal() {
  return {
    type: ACTIONS.CLOSE_TRANSFER_PREFERENCES_MODAL,
  }
}

function setEvoiceError(evoiceErrors) {
  return {
    type: ACTIONS.SET_EVOICE_ERROR,
    evoiceErrors
  }
}

function optOutOldEmail(email, preferences) {
  const updatedOldPreferences = setOldPreferencesToOptOut(preferences)
  if (email) {
    updateOldEmailPreferences(email, updatedOldPreferences);
  }
}


function getAlertMessage(origin) {
  let message = "Well done! You successfully changed your email address."
  if (origin === "emailEdit") {
    message = "Well done! You successfully changed your email address and verified your phone number."
  }
  return message
}

//endregion

const EmailEdit = (props) => {

  const { executeRecaptcha } = useGoogleReCaptcha();

  const { showLoading, hideLoading } = useContext(LoadingContext);
  const {apiContext} = useContext(ApiContext)
  const {autoToken} = apiContext

  const { account, setStale } = useCustomerAccount();
  const { addAlert } = useCommonContext();

  const {
    onDoneEditing,
    currentEmail,
    onEmailChangeSuccess,
  } = props;

  const [state, dispatch] = useReducer(reducer, initialState);

  function verifyOtpSuccess(emailEditContext) {
    addAlert(MESSAGE_TYPE.SUCCESS, getAlertMessage(emailEditContext.origin))
    return {
      type: ACTIONS.VERIFY_OTP_SUCCESS,
      emailEditContext
    }
  }

  function reducer(oldState, action) {
    const newState = { ...oldState };
    switch (action.type) {
      case ACTIONS.INIT_OTP_MODAL:
        newState.emailEditContext = action.emailEditContext;
        newState.showOtpModal = true;
        newState.email = action.emailEditContext.email;
        return newState;
      case ACTIONS.INIT_OTP_CHOICE:
        newState.emailEditContext = action.emailEditContext;
        newState.showOtpModal = true;
        return newState;
      case ACTIONS.VERIFY_OTP_SUCCESS:
        if (isNativeApp()) {
          // When user has a phone number, their new email will be under "newEmail", otherwise that'll be undefined and the new email
          // will be under "email"
          const newEmail = action.emailEditContext.newEmail || action.emailEditContext.email;
          postDataToEventBus("NATIVE_APP.EMAIL", { emailid: newEmail });
        }
        newState.showOtpModal = false;
        newState.oldEmail = currentEmail;
        newState.oldEmailPreferences = action.emailEditContext.oldEmailPreferences;
        newState.newEmailPreferences = action.emailEditContext.newEmailPreferences;
        newState.showTransferPreferencesModal = true;
        return newState;
      case ACTIONS.VERIFY_OTP_FAILURE:
        addAlert(MESSAGE_TYPE.DANGER, MESSAGE.OTP_ATTEMPTS_EXHAUSTED);
        return initialState;
      case ACTIONS.CLOSE_TRANSFER_PREFERENCES_MODAL:
        onDoneEditing();
        setStale();
        return initialState;
      case ACTIONS.CLOSE_MODAL:
        return initialState;
      case ACTIONS.SET_EVOICE_ERROR:
        newState.evoiceErrors = action.evoiceErrors;
        return newState;
      case ACTIONS.UPDATE_AUTH_ID:
        newState.emailEditContext.authId = action.payload.authId;
        return newState;
      default:
        throw new Error();
    }
  }


  const fetchNewEmailPreferences = (emailEditContext) => {
    const newEmail = emailEditContext.newEmail || emailEditContext.email;
    askForEmailPreferences(newEmail , executeRecaptcha).then(({ response }) => {
      if (response) {
        const { data } = response;
        if (data.subscriptions && hasPreferences(data.subscriptions)) {
          emailEditContext.newEmailPreferences = data.subscriptions;
        }
      }
      hideLoading();
      dispatch(verifyOtpSuccess(emailEditContext));
    });
  }

  const fetchOldEmailPreferences = (emailEditContext) => {
    const oldEmail = currentEmail
    askForEmailPreferences(oldEmail, executeRecaptcha).then(({ response }) => {
      if (response) {
        const { data } = response;
        if (data.subscriptions && hasPreferences(data.subscriptions)) {
          emailEditContext.oldEmail = oldEmail;
          emailEditContext.oldEmailPreferences = data.subscriptions;
          optOutOldEmail(oldEmail, data.subscriptions);
        }
      }
      fetchNewEmailPreferences(emailEditContext);
    });
  };

  const onEmailSubmitSuccess = (context) => dispatch(initVerifyOtpAction(context));
  const onEmailSubmitChoice = (context) => dispatch(initVerifyOtpChoiceModal(context));
  const onVerifyOtpSuccess = (ctx) => fetchOldEmailPreferences(ctx);
  const onVerifyOtpFailure = () => dispatch(verifyOtpFailure());
  const onCancelModal = () => dispatch(cancelModal());
  const resetFormState = () => onDoneEditing();
  const onCloseEmailPreferenceTransferModal = () => dispatch(closeEmailPreferenceTransferModal());

  const fireOverlayAnalyticsEvent = (otpChannel) => {
    let deliveryMethod;
    switch (otpChannel) {
      case "sms":
        deliveryMethod = "text";
        break;
      case "phone":
        deliveryMethod = "phone call";
        break;
      default:
        deliveryMethod = otpChannel;
    }
    analytics.postOverlayEvent(`email address edit code entry - ${deliveryMethod}`);
  }

  const initialOtpChoiceFlow = (currentEmail, newEmail, primaryPhoneNumber, otpChannel, setErrorMessage, isPhoneVerified) => {
    const emailEditContext = {};
    emailEditContext.otpChannel = otpChannel;
    emailEditContext.email = currentEmail;
    emailEditContext.phone = primaryPhoneNumber;
    emailEditContext.setErrorMessage = setErrorMessage;
    emailEditContext.isPhoneVerified = isPhoneVerified;
    emailEditContext.newEmail = newEmail;
    onEmailSubmitChoice(emailEditContext);
  }

  const initiateEmailChange = (newEmail, otpChannel, onError) => {
    return new Promise((resolve, reject) => {
      showLoading();
      askForEmailChange(currentEmail, newEmail, account.encryptVerifiedPhoneToken, otpChannel, autoToken).then(({
        response,
        error
      }) => {
        if (response) {
          const { data } = response;
          const emailEditContext = {};
          emailEditContext.backendHostName = data.backendHostName;
          emailEditContext.authId = data.authId;
          emailEditContext.userId = data.userId;
          if (data.phone) {
            emailEditContext.otpChannel = (otpChannel === "sms") ? "sms" : "phone";
            emailEditContext.phone = data.phone;
          } else {
            emailEditContext.otpChannel = "email";
          }
          emailEditContext.email = newEmail;
          onEmailSubmitSuccess(emailEditContext);
          fireOverlayAnalyticsEvent(emailEditContext.otpChannel);
          hideLoading();
          resolve();
        } else if (error) {
          hideLoading();
          const errorMessage =  handleApiError(error, "emailEditForm");
          onError([errorMessage]);
          reject();
        }
      });
    });
  };


  const onEvoiceError = (errorList) => dispatch(setEvoiceError(errorList));
  const onEvoiceRequest = () => initiateEmailChange(state.emailEditContext.newEmail, "phone", onEvoiceError);

  const updateAuthID = (authId) => {
    dispatch({
      type: ACTIONS.UPDATE_AUTH_ID,
      payload: {authId}
    });
  };

  const injectEpcModal = { ...props, onCloseEmailPreferenceTransferModal };
  const injectEmailForm = {
    ...props,
    onEmailSubmitSuccess,
    initiateEmailChange,
    initialOtpChoiceFlow,
    onCancel: onDoneEditing
  };
  const injectVerifyOtpForm = {
    ...props,
    onVerifyOtpSuccess,
    onVerifyOtpFailure,
    onCancelModal,
    initiateEmailChange,
    onEvoiceRequest,
    evoiceErrors: state.evoiceErrors
  };
  const { showOtpModal, emailEditContext, showTransferPreferencesModal } = state;
  const displayNewOtpModal = account.primaryPhoneAvailable;

  return (
    <>
      {showTransferPreferencesModal &&
        <EpcModal isOpen={showTransferPreferencesModal} emailEditContext={emailEditContext} {...injectEpcModal} />
      }
      <EmailForm {...injectEmailForm} />
      {showOtpModal && (
        (displayNewOtpModal)
          ? <NewVerifyOtpModal origin='emailEdit' isOpen={showOtpModal} onEmailChangeSuccess={onEmailChangeSuccess} emailEditContext={emailEditContext} resetFormState={resetFormState} {...injectVerifyOtpForm} />
          : <VerifyOtpModal isOpen={showOtpModal} emailEditContext={emailEditContext} {...injectVerifyOtpForm} updateAuthID={updateAuthID}/>
      )}
      <div className="col__12-12 u--paddingNone-horizontal"/>
    </>
  );
};


export default EmailEdit;
